import { useMemo } from "react";
import { useScope } from "~/hooks/useScope";
import {
  AssetOrder,
  AssetOrderField,
  OrderDirection,
  ScoreType,
  useAssetForwardPaginationQuery,
} from "~/operations";
import { useSort } from "~/pages/inventory/hooks/useSort";
import { usePaginate } from "~/components/pagination/usePaginate";
import { ASSET_TABLE_INITIAL_PAGE_RANGE } from "~/components/pagination";
import { useWorkflowAssetsFilterContext } from "~/pages/integrations/workflow/hooks/useWorkflowAssetsFilterContext";

export function useInitiativeAssets({
  initiativeMrn,
}: {
  initiativeMrn: string;
}) {
  const { spaceScope } = useScope();
  const paginate = usePaginate({
    defaultPageSize: ASSET_TABLE_INITIAL_PAGE_RANGE.to,
  });
  const baseUrl = paginate.resetCursorUrl(location.href);

  const sort = useSort<AssetOrder>({
    baseUrl,
    defaultSort: {
      field: AssetOrderField.LastUpdated,
      direction: OrderDirection.Desc,
    },
    validFields: [
      AssetOrderField.Name,
      AssetOrderField.LastUpdated,
      AssetOrderField.RiskValue,
    ],
  });

  const { filterForm, filterVariables, activeFilterCount, resetFilters } =
    useWorkflowAssetsFilterContext({
      scopeMrn: spaceScope?.mrn || "",
      workflowMrn: initiativeMrn,
    });

  // Combine label and annotation filters into queryTerms for the API
  const queryTerms = useMemo(() => {
    return [
      ...(filterVariables.queryTerms || []),
      ...(filterVariables.labels || []),
      ...(filterVariables.annotations || []),
    ];
  }, [filterVariables]);

  const { data, error, loading, fetchMore } = useAssetForwardPaginationQuery({
    variables: {
      initiativeMrn,
      scopeMrn: spaceScope?.mrn || "",
      ...paginate.pageVars,
      orderBy: sort.orderBy,
      queryTerms: queryTerms.length > 0 ? queryTerms : undefined,
      rating: filterVariables.rating,
      scoreType: ScoreType.Unknown,
    },
    skip: !spaceScope?.mrn || !initiativeMrn,
    fetchPolicy: "cache-and-network",
  });

  const assetsConnection =
    data?.assets?.__typename === "AssetsConnection" ? data.assets : null;
  const edges = assetsConnection?.edges || [];
  const assets = edges.flatMap((edge) => (edge.node ? [edge.node] : []));
  const totalCount = assetsConnection?.totalCount || 0;
  const pageInfo = assetsConnection?.pageInfo;

  return {
    loading,
    error,
    assets,
    totalCount,
    pageInfo,
    fetchMore,
    sort,
    paginate,
    filterForm,
    activeFilterCount,
    resetFilters,
  };
}
